import React, { useState } from 'react';
import {
  Box,
  Heading,
  HStack,
  VStack,
  Input,
  Select,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  InputGroup,
  InputLeftElement,
  Text,
  Card,
  CardBody,
  Flex,
  Spacer,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Spinner,
  Center,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  Button,
} from '@chakra-ui/react';
import {
  SearchIcon,
  ViewIcon,
  ChevronDownIcon,
  InfoIcon
} from '@chakra-ui/icons';
import { useQuery } from '@tanstack/react-query';
import { apiService } from '../services/api';
import { Contact, VIPStatus } from '../types/api';
import { useAuth } from '../contexts/AuthContext';

// ============================================
// SEPARATE COMPONENT: ContactsTable
// This component re-renders when data changes
// ============================================
interface ContactsTableProps {
  contactsData: any;
  isLoading: boolean;
  error: any;
  user: any;
  page: number;
  setPage: (page: number) => void;
}

const ContactsTable: React.FC<ContactsTableProps> = ({
  contactsData,
  isLoading,
  error,
  user,
  page,
  setPage
}) => {
  const getVIPStatusColor = (status: VIPStatus) => {
    switch (status) {
      case 'very_vip': return 'purple';
      case 'board_member': return 'red';
      case 'vip': return 'blue';
      case 'sponsor': return 'green';
      case 'media': return 'orange';
      case 'key_speaker': return 'teal';
      case 'special_guest': return 'pink';
      case 'none': return 'gray';
      default: return 'gray';
    }
  };

  const getVIPStatusLabel = (status: VIPStatus) => {
    switch (status) {
      case 'very_vip': return 'VIP+';
      case 'board_member': return 'Conseil';
      case 'vip': return 'VIP';
      case 'sponsor': return 'Sponsor';
      case 'media': return 'Média';
      case 'key_speaker': return 'Intervenant';
      case 'special_guest': return 'Invité spécial';
      case 'none': return 'Standard';
      default: return status;
    }
  };

  const formatLastSync = (dateString?: string) => {
    if (!dateString) return 'Jamais';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (isLoading) {
    return (
      <Center py={10}>
        <Spinner size="xl" color="blue.500" />
      </Center>
    );
  }

  if (error) {
    return (
      <Box>
        <Text color="red.500">Erreur lors du chargement des contacts</Text>
      </Box>
    );
  }

  return (
    <>
      {/* Contacts Table */}
      <Card>
        <CardBody p={0}>
          <Table variant="simple">
            <Thead>
              <Tr>
                <Th>Contact</Th>
                <Th>Organisation</Th>
                <Th>Statut VIP</Th>
                <Th>Protocole</Th>
                <Th>Conférence Presse</Th>
                <Th>Dernière sync</Th>
                <Th>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {contactsData?.items.map((contact: Contact) => (
                <Tr key={contact.id}>
                  <Td>
                    <VStack align="start" spacing={1}>
                      <Text fontWeight="medium">{contact.full_name}</Text>
                      <Text fontSize="sm" color="gray.600">
                        {contact.email || 'Pas d\'email'}
                      </Text>
                      {contact.position && (
                        <Text fontSize="sm" color="gray.500">
                          {contact.position}
                        </Text>
                      )}
                    </VStack>
                  </Td>
                  <Td>
                    <Text>{contact.organization || '-'}</Text>
                  </Td>
                  <Td>
                    <Badge colorScheme={getVIPStatusColor(contact.global_vip_status)}>
                      {getVIPStatusLabel(contact.global_vip_status)}
                    </Badge>
                  </Td>
                  <Td>
                    <VStack align="start" spacing={1}>
                      {contact.protocol_rank && (
                        <Badge colorScheme="blue" variant="outline" size="sm">
                          Rang {contact.protocol_rank}
                        </Badge>
                      )}
                      {contact.official_title && (
                        <Text fontSize="sm" color="gray.600">
                          {contact.official_title}
                        </Text>
                      )}
                    </VStack>
                  </Td>
                  <Td>
                    <Badge
                      colorScheme={contact.should_invite_press_conference ? 'green' : 'gray'}
                      variant={contact.should_invite_press_conference ? 'solid' : 'outline'}
                    >
                      {contact.should_invite_press_conference ? 'Oui' : 'Non'}
                    </Badge>
                  </Td>
                  <Td>
                    <Text fontSize="sm">
                      {formatLastSync(contact.last_crm_sync)}
                    </Text>
                  </Td>
                  <Td>
                    <Menu>
                      <MenuButton
                        as={IconButton}
                        icon={<ChevronDownIcon />}
                        variant="ghost"
                        size="sm"
                      />
                      <MenuList>
                        <MenuItem icon={<InfoIcon />}>
                          Voir les détails
                        </MenuItem>
                        {user?.can_create_events && (
                          <MenuItem icon={<ViewIcon />}>
                            Historique événements
                          </MenuItem>
                        )}
                      </MenuList>
                    </Menu>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>

          {contactsData?.items.length === 0 && (
            <Box p={8} textAlign="center">
              <Text color="gray.500">Aucun contact trouvé</Text>
            </Box>
          )}
        </CardBody>
      </Card>

      {/* Pagination */}
      {contactsData && contactsData.total_pages > 1 && (
        <Flex justify="center" mt={6}>
          <HStack>
            <Button
              isDisabled={page === 1}
              onClick={() => setPage(page - 1)}
            >
              Précédent
            </Button>
            <Text px={4}>
              Page {page} sur {contactsData.total_pages}
            </Text>
            <Button
              isDisabled={page === contactsData.total_pages}
              onClick={() => setPage(page + 1)}
            >
              Suivant
            </Button>
          </HStack>
        </Flex>
      )}
    </>
  );
};

// ============================================
// MAIN COMPONENT: ContactsPage
// This component does NOT re-render when typing
// ============================================
const ContactsPage: React.FC = () => {
  const { user } = useAuth();

  // State for filters and pagination
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [vipFilter, setVipFilter] = useState<VIPStatus | ''>('');
  const [organizationFilter, setOrganizationFilter] = useState('');
  const [pressConferenceFilter, setPressConferenceFilter] = useState<boolean | ''>('');

  // Fetch contacts - this triggers re-render ONLY in ContactsTable component
  const { data: contactsData, isLoading, error } = useQuery({
    queryKey: ['contacts', page, pageSize, searchQuery, statusFilter, vipFilter, organizationFilter, pressConferenceFilter],
    queryFn: () => apiService.getContacts({
      page,
      page_size: pageSize,
      q: searchQuery || undefined,
      status: statusFilter || undefined,
      vip_status: vipFilter || undefined,
      organization: organizationFilter || undefined,
      should_invite_press_conference: pressConferenceFilter !== '' ? pressConferenceFilter : undefined,
      sort_by: 'last_name',
      sort_order: 'asc'
    }),
  });

  // Fetch contact stats
  const { data: statsData } = useQuery({
    queryKey: ['contact-stats'],
    queryFn: () => apiService.getContactStats(),
  });

  // Fetch organizations for filter
  const { data: organizations } = useQuery({
    queryKey: ['contact-organizations'],
    queryFn: () => apiService.getContactOrganizations(),
  });

  return (
    <Box>
      {/* Header - NEVER re-renders */}
      <Flex mb={6} align="center">
        <VStack align="start" spacing={2}>
          <Heading>Contacts CRM</Heading>
          <Text color="gray.600" fontSize="sm">
            Contacts synchronisés depuis SuiteCRM (lecture seule)
          </Text>
        </VStack>
        <Spacer />
      </Flex>

      {/* Stats Cards - NEVER re-renders */}
      {statsData && (
        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6} mb={6}>
          <Card>
            <CardBody>
              <Stat>
                <StatLabel>Total Contacts</StatLabel>
                <StatNumber>{statsData.total_contacts}</StatNumber>
              </Stat>
            </CardBody>
          </Card>

          <Card>
            <CardBody>
              <Stat>
                <StatLabel>Contacts VIP</StatLabel>
                <StatNumber>{statsData.total_vips}</StatNumber>
              </Stat>
            </CardBody>
          </Card>

          <Card>
            <CardBody>
              <Stat>
                <StatLabel>Conférence Presse</StatLabel>
                <StatNumber>{statsData.press_conference_contacts}</StatNumber>
              </Stat>
            </CardBody>
          </Card>

          <Card>
            <CardBody>
              <Stat>
                <StatLabel>Organisations</StatLabel>
                <StatNumber>{statsData.total_organizations}</StatNumber>
              </Stat>
            </CardBody>
          </Card>
        </SimpleGrid>
      )}

      {/* Filters - NEVER re-renders */}
      <Card mb={6}>
        <CardBody>
          <VStack spacing={4}>
            <HStack spacing={4} wrap="wrap" w="full">
              <InputGroup flex="1" minW="200px">
                <InputLeftElement pointerEvents="none">
                  <SearchIcon color="gray.400" />
                </InputLeftElement>
                <Input
                  placeholder="Rechercher des contacts..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setPage(1);
                  }}
                  autoComplete="off"
                />
              </InputGroup>

              <Select
                placeholder="Statut VIP"
                value={vipFilter}
                onChange={(e) => {
                  setVipFilter(e.target.value as VIPStatus);
                  setPage(1);
                }}
                maxW="150px"
              >
                <option value="very_vip">VIP+</option>
                <option value="vip">VIP</option>
                <option value="board_member">Conseil</option>
                <option value="sponsor">Sponsor</option>
                <option value="media">Média</option>
                <option value="key_speaker">Intervenant</option>
                <option value="special_guest">Invité spécial</option>
                <option value="none">Standard</option>
              </Select>

              <Select
                placeholder="Organisation"
                value={organizationFilter}
                onChange={(e) => {
                  setOrganizationFilter(e.target.value);
                  setPage(1);
                }}
                maxW="200px"
              >
                {organizations?.map((org) => (
                  <option key={org} value={org}>
                    {org}
                  </option>
                ))}
              </Select>
            </HStack>

            <HStack spacing={4} wrap="wrap" w="full">
              <Select
                placeholder="Conférence de presse"
                value={pressConferenceFilter.toString()}
                onChange={(e) => {
                  setPressConferenceFilter(
                    e.target.value === 'true' ? true :
                    e.target.value === 'false' ? false : ''
                  );
                  setPage(1);
                }}
                maxW="200px"
              >
                <option value="true">Oui</option>
                <option value="false">Non</option>
              </Select>

              <Button
                variant="outline"
                onClick={() => {
                  setSearchQuery('');
                  setStatusFilter('');
                  setVipFilter('');
                  setOrganizationFilter('');
                  setPressConferenceFilter('');
                  setPage(1);
                }}
              >
                Réinitialiser
              </Button>
            </HStack>
          </VStack>
        </CardBody>
      </Card>

      {/* ContactsTable - ONLY THIS re-renders when data changes */}
      <ContactsTable
        contactsData={contactsData}
        isLoading={isLoading}
        error={error}
        user={user}
        page={page}
        setPage={setPage}
      />
    </Box>
  );
};

export default ContactsPage;